import { useCallback } from 'react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  RegistryInboundPage,
  RegistryInboundQuery,
  RegistryInboundRow,
  RegistryOrderDetail,
} from '@/types/registry-documents';
import { useRegistryPage } from './useRegistryPage';

/** Unmatched mail arrives with the delivery worker's sweeps, minutes
 * apart; a person acts on it within the hour, not the minute. */
export const REGISTRY_INBOUND_POLL_MS = 60_000;

const EMPTY: RegistryInboundPage = { items: [], total: 0, version: '' };

/**
 * A page of the emails from the research desk that reached no order,
 * polled with ETag revalidation like the order queue (`useRegistryPage`).
 *
 * `attach` and `dismiss` resolve an email and re-read the page, on failure
 * too: a refusal means someone else dealt with the email underneath the
 * screen, so the row shown is stale either way.
 */
export function useRegistryInboundMail(query: RegistryInboundQuery) {
  const { page, isLoading, error, refetch } = useRegistryPage(
    query,
    (sent, options) => vnextApiClient.listRegistryInboundMail(sent, options),
    REGISTRY_INBOUND_POLL_MS,
    EMPTY
  );

  const attach = useCallback(
    async (rowId: string, orderId: string): Promise<RegistryOrderDetail> => {
      try {
        return await vnextApiClient.attachRegistryInboundMail(rowId, orderId);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  const dismiss = useCallback(
    async (rowId: string, note?: string | null): Promise<RegistryInboundRow> => {
      try {
        return await vnextApiClient.dismissRegistryInboundMail(rowId, note);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  return { page, isLoading, error, refetch, attach, dismiss };
}
